import * as THREE from "three";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";

// Jitter and facets for the low-poly look (decision 0029): a shaped primitive's welded vertices are nudged by a
// seeded amount (so faces stay closed), the result goes non-indexed with flat normals, and each face gets the base
// colour times a small seeded tint, so neighbouring facets read apart. A `glow` attribute (0 lit, 1 emissive) rides
// along so lit and glowing parts can merge into one mesh; a material that reads it (for example @rpg/look's low-poly
// surface) shows glowing faces unlit. Same seed, same mesh.

/** A seeded generator of floats in [0, 1) (mulberry32). Same seed, same sequence. */
export function seeded(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The vertex attribute the facet helper writes for glowing faces (one float per vertex, 0 to 1). */
export const GLOW_ATTRIBUTE = "glow";

export interface FacetOptions {
  /** The base colour (linear, three's working space). */
  colour: THREE.Color;
  /** Metres: each welded vertex moves by up to this much on each axis. 0: none. */
  jitter?: number;
  /** Each face's colour is the base times 1 ± up to this. 0: one flat colour. */
  tint?: number;
  /** 0 lit, 1 glowing (written to every vertex). */
  glow?: number;
  /** The random source (default `seeded(1)`). */
  rng?: () => number;
}

/**
 * A faceted, vertex-coloured copy of `geo`: welded and jittered (when `jitter` > 0), non-indexed, flat normals,
 * a per-face tinted `color` and a `glow` attribute. Only position, normal, color and glow are kept, so the results
 * of any shapes merge (`merge`).
 */
export function facet(geo: THREE.BufferGeometry, o: FacetOptions): THREE.BufferGeometry {
  const rng = o.rng ?? seeded(1);
  let g = geo.clone();
  for (const k of Object.keys(g.attributes)) if (k !== "position") g.deleteAttribute(k);
  const jitter = o.jitter ?? 0;
  if (jitter > 0) {
    g = mergeVertices(g, 1e-4);
    const p = g.getAttribute("position");
    for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) + (rng() * 2 - 1) * jitter, p.getY(i) + (rng() * 2 - 1) * jitter, p.getZ(i) + (rng() * 2 - 1) * jitter);
  }
  if (g.index) g = g.toNonIndexed();
  g.computeVertexNormals();
  const n = g.getAttribute("position").count;
  const col = new Float32Array(n * 3);
  const tint = o.tint ?? 0;
  for (let f = 0; f < n; f += 3) {
    const k = tint > 0 ? 1 + (rng() * 2 - 1) * tint : 1;
    for (let v = 0; v < 3 && f + v < n; v++) {
      col[(f + v) * 3] = o.colour.r * k;
      col[(f + v) * 3 + 1] = o.colour.g * k;
      col[(f + v) * 3 + 2] = o.colour.b * k;
    }
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  g.setAttribute(GLOW_ATTRIBUTE, new THREE.BufferAttribute(new Float32Array(n).fill(o.glow ?? 0), 1));
  return g;
}

/** Mirror a geometry across x = 0 (positions and normals), keeping its faces' winding outward. */
export function mirrorX(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo.clone();
  g.applyMatrix4(new THREE.Matrix4().makeScale(-1, 1, 1));
  // a reflection reverses winding: swap the 2nd and 3rd vertex of every triangle in every attribute
  for (const a of Object.values(g.attributes) as THREE.BufferAttribute[]) {
    const s = a.itemSize;
    const arr = a.array;
    for (let f = 0; f + 2 < a.count; f += 3)
      for (let k = 0; k < s; k++) {
        const i1 = (f + 1) * s + k;
        const i2 = (f + 2) * s + k;
        const t = arr[i1]!;
        arr[i1] = arr[i2]!;
        arr[i2] = t;
      }
    a.needsUpdate = true;
  }
  return g;
}
